// localStorage wrapper that stores `{ value, expires }` so keys written
// without consent do not live forever. SSR-safe and never throws.

export const MS_PER_DAY = 24 * 60 * 60 * 1000;
// 13 months, expressed in days (the longest lifetime we keep without consent).
export const THIRTEEN_MONTHS_DAYS = 395;

type StoredEnvelope = { value: string; expires: number };

type ReadOptions = {
  // Strict: a value without `expires` (legacy format) is treated as expired.
  strict?: boolean;
  // Rewrites a legacy value as `{ value, expires }` with this lifetime.
  legacyMaxAgeDays?: number;
};

const getStorage = (): Storage | null => {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
};

const isEnvelope = (parsed: unknown): parsed is StoredEnvelope =>
  typeof parsed === 'object' &&
  parsed !== null &&
  typeof (parsed as StoredEnvelope).value === 'string' &&
  typeof (parsed as StoredEnvelope).expires === 'number';

const parseEnvelope = (raw: string): StoredEnvelope | null => {
  try {
    const parsed: unknown = JSON.parse(raw);
    return isEnvelope(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

export const removeStoredItem = (key: string): void => {
  try {
    getStorage()?.removeItem(key);
  } catch {
    // Storage unavailable: nothing to remove.
  }
};

/**
 * Reads a key. Expired values are removed and `null` is returned. Legacy
 * values without `expires` (raw strings such as "fr" or "true") are returned
 * as-is unless `strict` is set, in which case they are removed. With
 * `legacyMaxAgeDays`, a legacy value is also rewritten with an expiry.
 */
export const getStoredItem = (
  key: string,
  { strict = false, legacyMaxAgeDays }: ReadOptions = {}
): string | null => {
  try {
    const storage = getStorage();
    if (!storage) return null;
    const raw = storage.getItem(key);
    if (raw === null) return null;
    const envelope = parseEnvelope(raw);
    if (!envelope) {
      if (strict) {
        storage.removeItem(key);
        return null;
      }
      if (legacyMaxAgeDays !== undefined) {
        setStoredItem(key, raw, legacyMaxAgeDays);
      }
      return raw;
    }
    if (envelope.expires <= Date.now()) {
      storage.removeItem(key);
      return null;
    }
    return envelope.value;
  } catch {
    return null;
  }
};

/** Writes a key, renewing its expiry to now + maxAgeDays. */
export const setStoredItem = (
  key: string,
  value: string,
  maxAgeDays: number
): void => {
  try {
    const envelope: StoredEnvelope = {
      value,
      expires: Date.now() + maxAgeDays * MS_PER_DAY,
    };
    getStorage()?.setItem(key, JSON.stringify(envelope));
  } catch {
    // Storage full or blocked: the preference simply is not persisted.
  }
};

/**
 * Removes expired entries among the given exact keys or key prefixes.
 * Only entries in the `{ value, expires }` format are considered, so legacy
 * values are left alone until they are next rewritten.
 */
export const removeExpiredStoredItems = (keysOrPrefixes: string[]): void => {
  try {
    const storage = getStorage();
    if (!storage) return;
    const keys: string[] = [];
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i);
      if (key !== null) keys.push(key);
    }
    const now = Date.now();
    keys
      .filter((key) => keysOrPrefixes.some((p) => key === p || key.startsWith(p)))
      .forEach((key) => {
        const raw = storage.getItem(key);
        const envelope = raw === null ? null : parseEnvelope(raw);
        if (envelope && envelope.expires <= now) storage.removeItem(key);
      });
  } catch {
    // Ignore: cleanup is best effort.
  }
};
